import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Loader } from "rsuite";
import { axInst } from "../../../Components/Login/Service";
import WaterMark from "../../../Components/WaterMark/WaterMark.jsx";
import { IoIosArrowForward, IoIosArrowDown } from "react-icons/io";

import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

const vasPenetrationTableApi = async (body) => {
  const res = await axInst.post("dealer-review/vas_penetration", body);
  return res.data;
};

const fetchCollapsedRowData = async (body) => {
  const res = await axInst.post(
    "/dealer-review/silver_platinum_vas_penetration",
    body
  );
  return res.data;
};

const Vaspenetration = ({ productLine }) => {
  const months = [
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
    "Jan",
    "Feb",
    "Mar",
  ];

  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState();
  const [selOps, setSelOps] = useState({ Product_Line: [] });

  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRowData, setExpandedRowData] = useState({});

  const { data, submitted } = useSelector((e) => e.filtersDealerReview);

  const isOtherFilterApplied = (data) => {
    const keysToCheck = Object.keys(data).filter(
      (key) => key !== "Product_Line"
    );
    return keysToCheck.some((key) => data[key]?.length > 0);
  };

  const isProductLineFromRedux = data?.Product_Line?.length > 0;
  const otherFiltersApplied = isOtherFilterApplied(data);

  const finalProductLine = useMemo(() => {
    if (productLine) return [productLine];
    if (isProductLineFromRedux) {
      return data.Product_Line;
    }
    if (otherFiltersApplied) {
      return selectedOption ? [selectedOption] : [];
    }
    return selectedOption ? [selectedOption] : [];
  }, [
    productLine,
    data,
    isProductLineFromRedux,
    otherFiltersApplied,
    selectedOption,
  ]);

  const {
    data: rawData = {},
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["vasPenetration", submitted, finalProductLine],
    queryFn: () =>
      vasPenetrationTableApi({ ...data, Product_Line: finalProductLine }),
    cacheTime: 0,
    staleTime: 0,
  });

  const handleSelectionChange = (event) => {
    const selection = event.target.value;
    setSelectedOption(selection);
    setSelOps({ Product_Line: [selection] });
    setExpandedRows({});
    setExpandedRowData({});
  };

  const fetchOptions = async (data) => {
    try {
      const response = await axInst.post("/dealer-review/gtme_filter", data);
      const dropdownOptions = response.data || [];
      setOptions(dropdownOptions);
      const shouldSetDefault = !isProductLineFromRedux && !otherFiltersApplied;
      if (shouldSetDefault && dropdownOptions.length > 0) {
        const defaultOption = "";
        setSelectedOption(defaultOption);
        setSelOps({ Product_Line: [defaultOption] });
      }
      if (isProductLineFromRedux) {
        setSelectedOption(data.Product_Line[0]);
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  useEffect(() => {
    fetchOptions(data);
  }, [submitted]);

  useEffect(() => {
    if (productLine) {
      setSelectedOption(productLine);
      setSelOps({ Product_Line: [productLine] });
    }
  }, [productLine]);

  useEffect(() => {
    if (selectedOption) {
      refetch();
    }
  }, [selectedOption, refetch]);

  const tableData = useMemo(() => {
    const rows = {};
    months.forEach((month) => {
      const monthData = rawData[month];
      if (monthData) {
        Object.entries(monthData).forEach(([category, value]) => {
          if (!rows[category]) {
            rows[category] = { label: category };
          }
          rows[category][month] = value;
        });
      }
    });
    return Object.values(rows).map((row) => {
      months.forEach((month) => {
        if (row[month] === undefined) {
          row[month] = "-";
        }
      });
      return row;
    });
  }, [rawData]);

  const totalsRow = useMemo(() => {
    const totalRow = { label: "Total" };
    months.forEach((month) => {
      let sum = 0;
      tableData.forEach((row) => {
        const val = row[month];
        if (typeof val === "number") {
          sum += val;
        } else if (typeof val === "string" && val.includes("%")) {
          const numeric = parseFloat(val.replace("%", ""));
          if (!isNaN(numeric)) sum += numeric;
        }
      });
      totalRow[month] = `${sum.toFixed(1)}%`;
    });
    return totalRow;
  }, [tableData]);

  const getExpandedContent = (rowLabel, expandData) => {
    if (!expandData) return [];

    if (rowLabel === "FMS") {
      return Object.entries(
        months.reduce((acc, month) => {
          const monthData = expandData[month] || {};
          Object.entries(monthData).forEach(([category, value]) => {
            if (category.toLowerCase() === "silver") return;
            if (!acc[category]) acc[category] = {};
            acc[category][month] = value;
          });
          return acc;
        }, {})
      );
    } else {
      return Object.entries(
        months.reduce((acc, month) => {
          const monthData = expandData[month] || {};
          Object.entries(monthData).forEach(([category, value]) => {
            if (!acc[category]) acc[category] = {};
            acc[category][month] = value;
          });
          return acc;
        }, {})
      );
    }
  };

  const columns = useMemo(() => {
    return [
      {
        accessorKey: "label",
        header: "",
        cell: (info) => info.getValue(),
      },
      ...months.map((month) => ({
        accessorKey: month,
        header: month,
        cell: (info) => info.getValue() ?? "-",
      })),
    ];
  }, []);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = async (rowLabel) => {
    if (rowLabel === "Total") return;

    const alreadyExpanded = expandedRows[rowLabel];
    setExpandedRows((prev) => ({
      ...prev,
      [rowLabel]: !prev[rowLabel],
    }));

    if (!alreadyExpanded && !expandedRowData[rowLabel]) {
      try {
        const res = await fetchCollapsedRowData({
          ...data,
          vas_status: [rowLabel],
          Product_Line: finalProductLine,
        });
        setExpandedRowData((prev) => ({
          ...prev,
          [rowLabel]: res,
        }));
      } catch (err) {
        console.error("Error fetching expanded row data:", err);
      }
    }
  };

  return (
    <div className="relative">
      <WaterMark />
      <div className="bg-white p-2 rounded-md">
        <div className="flex justify-between">
          <h2 className="text-lg font-bold text-gray-800">
            VAS Penetration {productLine && `- ${productLine}`}
          </h2>
          {!productLine && (
            <select
              value={selectedOption || ""}
              onChange={handleSelectionChange}
              className="border border-gray-300 text-sm rounded-md w-40 p-1"
            >
              <option value="">All Product Line</option>
              {options.map((option, index) => (
                <option key={index} value={option.key}>
                  {option.key}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="drilltable">
          {isFetching ? (
            <div className="grid items-center justify-center w-full h-full">
              <Loader />
            </div>
          ) : isError ? (
            <div className="grid items-center justify-center w-full h-full">
              Error loading table data.
            </div>
          ) : tableData.length === 0 ? (
            <div className="grid items-center justify-center w-full h-full">
              No data available.
            </div>
          ) : (
            <table className="min-w-full border border-gray-300 text-sm mt-2">
              <thead className="bg-gray-100">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-3 py-1 border border-gray-300 text-white text-xs bg-[#28536F]"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => {
                  const rowLabel = row.getValue("label");
                  const isExpanded = expandedRows[rowLabel];
                  const expandData = expandedRowData[rowLabel];

                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        onClick={() =>
                          rowLabel !== "Total" && handleRowClick(rowLabel)
                        }
                        className={rowLabel !== "Total" ? "cursor-pointer" : ""}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const isLabelColumn = cell.column.id === "label";
                          const rawValue = cell.getValue();
                          const displayValue =
                            isLabelColumn ||
                            rowLabel === "Total" ||
                            (typeof rawValue === "string" &&
                              rawValue.includes("-"))
                              ? rawValue
                              : `${rawValue}%`;

                          return (
                            <td
                              key={cell.id}
                              className={`p-2 border border-gray-200 text-center text-xs ${
                                isLabelColumn
                                  ? "bg-[#D4E1EC] font-bold text-left pl-2"
                                  : "bg-white"
                              }`}
                            >
                              {isLabelColumn ? (
                                <span className="flex items-center justify-between">
                                  {displayValue}
                                  {rowLabel !== "Total" && (
                                    <span className="ml-1">
                                      {expandedRows[rowLabel] ? (
                                        <IoIosArrowDown />
                                      ) : (
                                        <IoIosArrowForward />
                                      )}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                displayValue
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {isExpanded &&
                        expandData &&
                        getExpandedContent(rowLabel, expandData).map(
                          ([category, monthValues]) => (
                            <tr key={`${rowLabel}-${category}`}>
                              <td className="p-2 border border-gray-200 text-center text-xs bg-[#E3F2FD] font-bold">
                                {category}
                              </td>
                              {months.map((month) => (
                                <td
                                  key={`${rowLabel}-${category}-${month}`}
                                  className="p-2 border border-gray-200 text-center text-xs bg-white"
                                >
                                  {monthValues[month] !== undefined
                                    ? `${monthValues[month]}%`
                                    : "-"}
                                </td>
                              ))}
                            </tr>
                          )
                        )}
                    </React.Fragment>
                  );
                })}
                <tr className="bg-gray-100 font-bold">
                  <td className="p-2 border border-gray-300 text-left text-xs bg-[#B0C4DE]">
                    {totalsRow.label}
                  </td>
                  {months.map((month) => (
                    <td
                      key={`total-${month}`}
                      className="p-2 border border-gray-300 text-center text-xs bg-[#F0F8FF]"
                    >
                      {totalsRow[month]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Vaspenetration;
